import React from "react";
import styled from "styled-components";
import { defineMessages, useIntl } from "react-intl";
import { FixedObject } from "gatsby-image";
import { HowItWorksStep } from "./HowItWorks/HowItWorksStep";
import { AlternateBackground } from "./HowItWorks/AlternateBackground";
import { WideContainer } from "../../shared/components/WideContainer";

const messages = defineMessages({
  title: {
    id: "HowItWorks.title",
    defaultMessage: "How it works",
  },
  stepOneTitle: {
    id: "HowItWorks.stepOneTitle",
    defaultMessage: "Describe Your Task",
  },
  stepOneDescription: {
    id: "HowItWorks.stepOneDescription",
    defaultMessage:
      "Tell us what you need done, when and where it works for you.",
  },
  stepTwoTitle: {
    id: "HowItWorks.stepTwoTitle",
    defaultMessage: "Choose Your Tasker",
  },
  stepTwoDescription: {
    id: "HowItWorks.stepTwoDescription",
    defaultMessage:
      "Browse trusted Taskers by skills, reviews, and price. Chat with them to confirm details.",
  },
  stepThreeTitle: {
    id: "HowItWorks.stepThreeTitle",
    defaultMessage: "Get It Done!",
  },
  stepThreeDescription: {
    id: "HowItWorks.stepThreeDescription",
    defaultMessage:
      "Your Tasker arrives and gets the job done. Pay securely and leave a review, all through TaskRabbit.",
  },
});

export interface HowItWorksProps {
  stepOneFixed: FixedObject;
  stepTwoFixed: FixedObject;
  stepThreeFixed: FixedObject;
}

export function HowItWorks({
  stepOneFixed,
  stepTwoFixed,
  stepThreeFixed,
}: HowItWorksProps) {
  const intl = useIntl();

  return (
    <AlternateBackground>
      <VerticalPadding>
        <WideContainer>
          <TitleContainer>
            <Title>{intl.formatMessage(messages.title)}</Title>
          </TitleContainer>
          <StepsContainer>
            <HowItWorksStep
              fixed={stepOneFixed}
              position={1}
              title={intl.formatMessage(messages.stepOneTitle)}
              description={intl.formatMessage(messages.stepOneDescription)}
            />
            <TabletDesktopSpacer />
            <HowItWorksStep
              fixed={stepTwoFixed}
              position={2}
              title={intl.formatMessage(messages.stepTwoTitle)}
              description={intl.formatMessage(messages.stepTwoDescription)}
            />
            <TabletDesktopSpacer />
            <HowItWorksStep
              fixed={stepThreeFixed}
              position={3}
              title={intl.formatMessage(messages.stepThreeTitle)}
              description={intl.formatMessage(messages.stepThreeDescription)}
            />
          </StepsContainer>
        </WideContainer>
      </VerticalPadding>
    </AlternateBackground>
  );
}

const VerticalPadding = styled.div`
  padding-top: ${(props) => props.theme.spacing.xxl};
  padding-bottom: ${(props) => props.theme.spacing.xxl};
`;

const TabletDesktopSpacer = styled.div`
  ${(props) => props.theme.breakpoints.tabletUp} {
    flex-basis: ${(props) => props.theme.spacing.lg};
    flex-shrink: 0;
  }
`;

const TitleContainer = styled.div`
  justify-content: center;
  display: flex;
  width: 100%;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  ${(props) => props.theme.breakpoints.desktopUp} {
    flex-direction: row;
  }
  width: 100%;
`;

const Title = styled.div`
  font-weight: ${(props) => props.theme.typography.weight.extraBold};
  margin-bottom: ${(props) => props.theme.spacing.md};
  font-size: ${(props) => props.theme.typography.typeScale[400]};
`;
